import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toCamelCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const clientAnalyticsService = {
  async getClientRevenueData(filters = {}) {
    try {
      let query = supabase
        ?.from('work_orders')
        ?.select(`
          id,
          folio_ot,
          fecha_ingreso,
          total,
          estado_ot,
          client:clients(
            id,
            nombre
          ),
          vehicle:vehicles(
            tipo
          )
        `)
        ?.eq('estado_ot', 'entregado');

      // Apply date range filter
      if (filters?.startDate) {
        query = query?.gte('fecha_ingreso', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('fecha_ingreso', filters?.endDate);
      }

      const { data, error } = await query?.order('fecha_ingreso', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching client revenue data:', error?.message);
        return [];
      }

      const workOrders = toCamelCase(data || []);

      // Aggregate by client
      const clientMap = {};
      workOrders?.forEach(order => {
        const clientId = order?.client?.id;
        const clientName = order?.client?.nombre || 'Cliente Desconocido';
        const clientType = order?.client?.tipoCliente || 'individual';
        const revenue = parseFloat(order?.total || 0);

        if (!clientMap?.[clientId]) {
          clientMap[clientId] = {
            clientId,
            clientName,
            clientType,
            totalRevenue: 0,
            serviceCount: 0,
            averageInvoiceValue: 0,
            lastServiceDate: order?.fechaIngreso,
            clientLifetimeValue: 0,
            orders: []
          };
        }

        clientMap[clientId].totalRevenue += revenue;
        clientMap[clientId].serviceCount += 1;
        clientMap[clientId].clientLifetimeValue += revenue;
        clientMap?.[clientId]?.orders?.push(order);

        // Update last service date
        if (new Date(order?.fechaIngreso) > new Date(clientMap[clientId].lastServiceDate)) {
          clientMap[clientId].lastServiceDate = order?.fechaIngreso;
        }
      });

      // Calculate averages and apply filters
      let clientStats = Object.values(clientMap)?.map(client => ({
        ...client,
        averageInvoiceValue: client?.serviceCount > 0 ? client?.totalRevenue / client?.serviceCount : 0
      }));

      // Apply client type filter
      if (filters?.clientType && filters?.clientType !== 'all') {
        clientStats = clientStats?.filter(client => client?.clientType === filters?.clientType);
      }

      // Apply revenue threshold filter
      if (filters?.minRevenue) {
        clientStats = clientStats?.filter(client => client?.totalRevenue >= parseFloat(filters?.minRevenue));
      }

      return clientStats;
    } catch (error) {
      console.error('Failed to fetch client revenue data:', error);
      throw error;
    }
  },

  async getRepeatServiceRate(filters = {}) {
    try {
      let query = supabase
        ?.from('work_orders')
        ?.select(`
          id,
          fecha_ingreso,
          client:clients(
            id,
            nombre
          )
        `)
        ?.eq('estado_ot', 'entregado');

      // Apply date range filter
      if (filters?.startDate) {
        query = query?.gte('fecha_ingreso', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('fecha_ingreso', filters?.endDate);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return { repeatRate: 0, repeatClients: 0, totalClients: 0 };
        console.log('Error fetching repeat service data:', error?.message);
        return { repeatRate: 0, repeatClients: 0, totalClients: 0 };
      }

      const workOrders = toCamelCase(data || []);

      // Count services per client
      const clientServiceCount = {};
      workOrders?.forEach(order => {
        const clientId = order?.client?.id;
        if (!clientServiceCount?.[clientId]) {
          clientServiceCount[clientId] = 0;
        }
        clientServiceCount[clientId] += 1;
      });

      const totalClients = Object.keys(clientServiceCount)?.length;
      const repeatClients = Object.values(clientServiceCount)?.filter(count => count > 1)?.length;
      const repeatRate = totalClients > 0 ? (repeatClients / totalClients) * 100 : 0;

      return {
        repeatRate,
        repeatClients,
        totalClients,
        oneTimeClients: totalClients - repeatClients
      };
    } catch (error) {
      console.error('Failed to calculate repeat service rate:', error);
      throw error;
    }
  },

  async getClientDistributionByRevenue(filters = {}) {
    try {
      const clientData = await this.getClientRevenueData(filters);

      // Define revenue tiers
      const tiers = [
        { name: '$0 - $5,000', min: 0, max: 5000, count: 0, revenue: 0 },
        { name: '$5,001 - $15,000', min: 5001, max: 15000, count: 0, revenue: 0 },
        { name: '$15,001 - $30,000', min: 15001, max: 30000, count: 0, revenue: 0 },
        { name: '$30,001 - $50,000', min: 30001, max: 50000, count: 0, revenue: 0 },
        { name: '$50,001+', min: 50001, max: Infinity, count: 0, revenue: 0 }
      ];

      clientData?.forEach(client => {
        const revenue = client?.totalRevenue;
        const tier = tiers?.find(t => revenue >= t?.min && revenue <= t?.max);
        if (tier) {
          tier.count += 1;
          tier.revenue += revenue;
        }
      });

      return tiers?.filter(tier => tier?.count > 0);
    } catch (error) {
      console.error('Failed to get client distribution by revenue:', error);
      throw error;
    }
  },

  async getServiceFrequencyPatterns(filters = {}) {
    try {
      const clientData = await this.getClientRevenueData(filters);

      // Define service frequency categories
      const patterns = [
        { name: '1 servicio', min: 1, max: 1, count: 0 },
        { name: '2-3 servicios', min: 2, max: 3, count: 0 },
        { name: '4-6 servicios', min: 4, max: 6, count: 0 },
        { name: '7-10 servicios', min: 7, max: 10, count: 0 },
        { name: '11+ servicios', min: 11, max: Infinity, count: 0 }
      ];

      clientData?.forEach(client => {
        const serviceCount = client?.serviceCount;
        const pattern = patterns?.find(p => serviceCount >= p?.min && serviceCount <= p?.max);
        if (pattern) {
          pattern.count += 1;
        }
      });

      return patterns?.filter(pattern => pattern?.count > 0);
    } catch (error) {
      console.error('Failed to get service frequency patterns:', error);
      throw error;
    }
  },

  async getTopValueClients(limit = 10, filters = {}) {
    try {
      const clientData = await this.getClientRevenueData(filters);
      return clientData?.sort((a, b) => b?.clientLifetimeValue - a?.clientLifetimeValue)?.slice(0, limit);
    } catch (error) {
      console.error('Failed to get top value clients:', error);
      throw error;
    }
  },

  async getClientEmailCommunicationLogs(filters = {}) {
    try {
      let query = supabase
        ?.from('client_email_logs')
        ?.select(`
          id,
          client_id,
          schedule_id,
          report_type,
          subject,
          recipient_email,
          status,
          sent_at,
          opened_at,
          clicked_at,
          open_count,
          click_count,
          error_message,
          client:clients(
            id,
            nombre
          ),
          schedule:email_schedules(
            report_name
          )
        `)
        ?.order('sent_at', { ascending: false });

      // Apply date range filter
      if (filters?.startDate) {
        query = query?.gte('sent_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('sent_at', filters?.endDate);
      }

      // Apply client filter
      if (filters?.clientId) {
        query = query?.eq('client_id', filters?.clientId);
      }

      // Apply report type filter
      if (filters?.reportType && filters?.reportType !== 'all') {
        query = query?.eq('report_type', filters?.reportType);
      }

      // Apply status filter
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching client email logs:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch client email communication logs:', error);
      throw error;
    }
  },

  async getClientEmailEngagementMetrics(filters = {}) {
    try {
      const logs = await this.getClientEmailCommunicationLogs(filters);

      const totalSent = logs?.length;
      const totalOpened = logs?.filter(log => log?.openedAt)?.length;
      const totalClicked = logs?.filter(log => log?.clickedAt)?.length;
      const totalFailed = logs?.filter(log => log?.status === 'failed')?.length;

      const openRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;
      const clickRate = totalSent > 0 ? (totalClicked / totalSent) * 100 : 0;
      const deliveryRate = totalSent > 0 ? ((totalSent - totalFailed) / totalSent) * 100 : 0;

      // Group by client
      const clientEngagement = {};
      logs?.forEach(log => {
        const clientId = log?.clientId;
        const clientName = log?.client?.nombre || 'Unknown Client';

        if (!clientEngagement?.[clientId]) {
          clientEngagement[clientId] = {
            clientId,
            clientName,
            totalSent: 0,
            totalOpened: 0,
            totalClicked: 0,
            totalFailed: 0,
            openRate: 0,
            clickRate: 0,
            deliveryRate: 0
          };
        }

        clientEngagement[clientId].totalSent += 1;
        if (log?.openedAt) clientEngagement[clientId].totalOpened += 1;
        if (log?.clickedAt) clientEngagement[clientId].totalClicked += 1;
        if (log?.status === 'failed') clientEngagement[clientId].totalFailed += 1;
      });

      // Calculate rates for each client
      Object.values(clientEngagement)?.forEach(client => {
        client.openRate = client?.totalSent > 0 ? (client?.totalOpened / client?.totalSent) * 100 : 0;
        client.clickRate = client?.totalSent > 0 ? (client?.totalClicked / client?.totalSent) * 100 : 0;
        client.deliveryRate = client?.totalSent > 0 ? ((client?.totalSent - client?.totalFailed) / client?.totalSent) * 100 : 0;
      });

      return {
        overall: {
          totalSent,
          totalOpened,
          totalClicked,
          totalFailed,
          openRate,
          clickRate,
          deliveryRate
        },
        byClient: Object.values(clientEngagement)
      };
    } catch (error) {
      console.error('Failed to get client email engagement metrics:', error);
      throw error;
    }
  }
};